// ============================================
// Canvas Configuration
// ============================================

export const CANVAS_CONFIG = {
  WIDTH: 128,
  HEIGHT: 128,
  RESET_TIMEZONE: 'America/New_York', // EST
  RESET_HOUR: 0, // Midnight
  MAX_ZOOM: 35, // Maximum zoom level (in hundreds)
} as const;

export const CANVAS_EXPANSION_CONFIG = {
  COMPLETION_THRESHOLD: 0.85,
  EXPAND_BY_PIXELS: 32,
  CLIENT_BATCH_DELAY_MS: 120,
  MAX_BATCH_SIZE: 20,
} as const;

// ============================================
// Legacy Ink / Pixel Progress Configuration
// ============================================

export const INK_CONFIG = {
  DAILY_REFILL: 80, // Legacy value kept for existing achievement/account data
  COST_PER_PIXEL: 1, // Progress credited per pixel placed
} as const;

// ============================================
// User Configuration
// ============================================

export const USER_CONFIG = {
  USERNAME_MIN_LENGTH: 3,
  USERNAME_MAX_LENGTH: 16,
} as const;

// Email verification
export const VERIFICATION_CONFIG = {
  /** Seconds to wait before allowing another verification email (resend cooldown) */
  RESEND_COOLDOWN_SECONDS: 90,
} as const;

/** Password reset: token expiry and request cooldown */
export const PASSWORD_RESET_CONFIG = {
  /** Token valid for this many hours */
  TOKEN_EXPIRY_HOURS: 1,
  /** Seconds before another reset email can be requested for the same email */
  RESEND_COOLDOWN_SECONDS: 120,
} as const;

// ============================================
// Achievements
// ============================================

export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export type AchievementRewardType = 'badge' | 'name_color';
export type UsernameEffect = 'gold_shimmer';

export const NAME_COLOR_REWARDS = [
  {
    id: 'dark_green',
    label: 'Dark Green',
    color: '#166534',
  },
  {
    id: 'champagne',
    label: 'Champagne',
    color: '#FFF8E7',
  },
  {
    id: 'gold',
    label: 'Gold',
    color: '#D4AF37',
  },
  {
    id: 'light_blue',
    label: 'Light Blue',
    color: '#A5F3FC',
  },
  {
    id: 'deep_teal',
    label: 'Deep Teal',
    color: '#0E7490',
  },
] as const;

export type NameColorId = (typeof NAME_COLOR_REWARDS)[number]['id'];

export interface NameColorReward {
  id: NameColorId;
  label: string;
  color: string;
}

export const ACHIEVEMENT_DEFINITIONS = [
  {
    id: 'ink_spent_50',
    title: 'First 50',
    description: 'Place 50 pixels on the canvas.',
    target: 50,
    kind: 'ink_spent',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'INK50',
  },
  {
    id: 'ink_spent_250',
    title: 'Brush Marathon',
    description: 'Place 250 pixels on the canvas.',
    target: 250,
    kind: 'ink_spent',
    rarity: 'uncommon',
    revealedBy: 'ink_spent_50',
    badgeEmoji: 'INK250',
  },
  {
    id: 'ink_spent_800',
    title: 'Color Surge',
    description: 'Place 800 pixels on the canvas.',
    target: 800,
    kind: 'ink_spent',
    rarity: 'rare',
    revealedBy: 'ink_spent_250',
    badgeEmoji: 'INK800',
  },
  {
    id: 'ink_spent_1500',
    title: 'Master Painter',
    description: 'Place 1,500 pixels on the canvas.',
    target: 1500,
    kind: 'ink_spent',
    rarity: 'epic',
    revealedBy: 'ink_spent_800',
    badgeEmoji: 'INK1500',
  },
  {
    id: 'ink_spent_4000',
    title: 'Wall Legend',
    description: 'Place 4,000 pixels on the canvas.',
    target: 4000,
    kind: 'ink_spent',
    rarity: 'legendary',
    revealedBy: 'ink_spent_1500',
    badgeEmoji: 'INK4000',
  },
  {
    id: 'login_streak_3',
    title: 'Warmup Streak',
    description: 'Connect 3 days in a row.',
    target: 3,
    kind: 'login_streak',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'STREAK3',
  },
  {
    id: 'login_streak_7',
    title: 'Week Keeper',
    description: 'Connect 7 days in a row.',
    target: 7,
    kind: 'login_streak',
    rarity: 'uncommon',
    revealedBy: 'login_streak_3',
    badgeEmoji: 'STREAK7',
  },
  {
    id: 'login_streak_15',
    title: 'Half-Moon Habit',
    description: 'Connect 15 days in a row.',
    target: 15,
    kind: 'login_streak',
    rarity: 'rare',
    revealedBy: 'login_streak_7',
    badgeEmoji: 'STREAK15',
  },
  {
    id: 'login_streak_30',
    title: 'Unbroken Month',
    description: 'Connect 30 days in a row.',
    target: 30,
    kind: 'login_streak',
    rarity: 'epic',
    revealedBy: 'login_streak_15',
    badgeEmoji: 'STREAK30',
  },
  {
    id: 'login_streak_60',
    title: 'Streak Vanguard',
    description: 'Connect 60 days in a row.',
    target: 60,
    kind: 'login_streak',
    rarity: 'legendary',
    revealedBy: 'login_streak_30',
    badgeEmoji: 'STREAK60',
  },
  {
    id: 'turf_war_15',
    title: 'Turf War!',
    description: "Replace another player's pixel 15 times.",
    target: 15,
    kind: 'turf_war',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'TURF15',
  },
  {
    id: 'turf_war_50',
    title: 'Border Skirmish',
    description: "Replace another player's pixel 50 times.",
    target: 50,
    kind: 'turf_war',
    rarity: 'uncommon',
    revealedBy: 'turf_war_15',
    badgeEmoji: 'TURF50',
  },
  {
    id: 'turf_war_100',
    title: 'Territory Tactician',
    description: "Replace another player's pixel 100 times.",
    target: 100,
    kind: 'turf_war',
    rarity: 'rare',
    revealedBy: 'turf_war_50',
    badgeEmoji: 'TURF100',
  },
  {
    id: 'turf_war_200',
    title: 'Siege Artist',
    description: "Replace another player's pixel 200 times.",
    target: 200,
    kind: 'turf_war',
    rarity: 'epic',
    revealedBy: 'turf_war_100',
    badgeEmoji: 'TURF200',
  },
  {
    id: 'turf_war_500',
    title: 'Canvas Conqueror',
    description: "Replace another player's pixel 500 times.",
    target: 500,
    kind: 'turf_war',
    rarity: 'legendary',
    revealedBy: 'turf_war_200',
    badgeEmoji: 'TURF500',
  },
  {
    id: 'chat_messages_20',
    title: 'First Voice',
    description: 'Send 20 chat messages.',
    target: 20,
    kind: 'chat_messages',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'CHAT20',
  },
  {
    id: 'chat_messages_50',
    title: 'Conversation Starter',
    description: 'Send 50 chat messages.',
    target: 50,
    kind: 'chat_messages',
    rarity: 'uncommon',
    revealedBy: 'chat_messages_20',
    badgeEmoji: 'CHAT50',
  },
  {
    id: 'chat_messages_100',
    title: 'Community Regular',
    description: 'Send 100 chat messages.',
    target: 100,
    kind: 'chat_messages',
    rarity: 'rare',
    revealedBy: 'chat_messages_50',
    badgeEmoji: 'CHAT100',
  },
  {
    id: 'chat_messages_250',
    title: 'Thread Weaver',
    description: 'Send 250 chat messages.',
    target: 250,
    kind: 'chat_messages',
    rarity: 'epic',
    revealedBy: 'chat_messages_100',
    badgeEmoji: 'CHAT250',
  },
  {
    id: 'chat_messages_800',
    title: 'Wall Broadcaster',
    description: 'Send 800 chat messages.',
    target: 800,
    kind: 'chat_messages',
    rarity: 'legendary',
    revealedBy: 'chat_messages_250',
    badgeEmoji: 'CHAT800',
  },
  {
    id: 'expansion_witness',
    title: 'Expansion Witness',
    description: 'Be online when the canvas expands.',
    target: 1,
    kind: 'canvas_expansion_witness',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'EXPWITNESS',
  },
  {
    id: 'canvas_detonator',
    title: 'Canvas Detonator',
    description: 'Place the pixel that triggers a canvas expansion.',
    target: 1,
    kind: 'canvas_expansion_trigger',
    rarity: 'rare',
    revealedBy: null,
    badgeEmoji: 'DETONATOR',
  },
  {
    id: 'boom_maker',
    title: 'Boom Maker',
    description: 'Trigger 3 canvas expansions.',
    target: 3,
    kind: 'canvas_expansion_trigger',
    rarity: 'epic',
    revealedBy: 'canvas_detonator',
    badgeEmoji: 'BOOM3',
  },
  {
    id: 'big_bang_artist',
    title: 'Big Bang Artist',
    description: 'Trigger 10 canvas expansions.',
    target: 10,
    kind: 'canvas_expansion_trigger',
    rarity: 'legendary',
    revealedBy: 'boom_maker',
    badgeEmoji: 'BOOM10',
  },
  {
    id: 'daily_ink_zero_3',
    title: 'Empty Cartridge',
    description: 'Legacy ink-system milestone from 3 completed daily ink days.',
    target: 3,
    kind: 'daily_ink_zero_days',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'INKZERO3',
  },
  {
    id: 'daily_ink_zero_7',
    title: 'Ink Burner',
    description: 'Legacy ink-system milestone from 7 completed daily ink days.',
    target: 7,
    kind: 'daily_ink_zero_days',
    rarity: 'uncommon',
    revealedBy: 'daily_ink_zero_3',
    badgeEmoji: 'INKZERO7',
  },
  {
    id: 'daily_ink_zero_15',
    title: 'Full-Use Habit',
    description: 'Legacy ink-system milestone from 15 completed daily ink days.',
    target: 15,
    kind: 'daily_ink_zero_days',
    rarity: 'rare',
    revealedBy: 'daily_ink_zero_7',
    badgeEmoji: 'INKZERO15',
  },
  {
    id: 'daily_ink_zero_30',
    title: 'No Drop Wasted',
    description: 'Legacy ink-system milestone from 30 completed daily ink days.',
    target: 30,
    kind: 'daily_ink_zero_days',
    rarity: 'epic',
    revealedBy: 'daily_ink_zero_15',
    badgeEmoji: 'INKZERO30',
  },
  {
    id: 'daily_ink_zero_50',
    title: 'Ink Perfectionist',
    description: 'Legacy ink-system milestone from 50 completed daily ink days.',
    target: 50,
    kind: 'daily_ink_zero_days',
    rarity: 'legendary',
    revealedBy: 'daily_ink_zero_30',
    badgeEmoji: 'INKZERO50',
  },
  {
    id: 'archive_comments_5',
    title: 'Comment Curator',
    description: 'Post 5 archive comments.',
    target: 5,
    kind: 'archive_comments',
    rarity: 'common',
    revealedBy: null,
    badgeEmoji: 'COMMENT5',
  },
  {
    id: 'archive_comments_15',
    title: 'Thread Builder',
    description: 'Post 15 archive comments.',
    target: 15,
    kind: 'archive_comments',
    rarity: 'uncommon',
    revealedBy: 'archive_comments_5',
    badgeEmoji: 'COMMENT15',
  },
  {
    id: 'archive_comments_25',
    title: 'Conversation Architect',
    description: 'Post 25 archive comments.',
    target: 25,
    kind: 'archive_comments',
    rarity: 'rare',
    revealedBy: 'archive_comments_15',
    badgeEmoji: 'COMMENT25',
  },
  {
    id: 'archive_comments_50',
    title: 'Forum Mainstay',
    description: 'Post 50 archive comments.',
    target: 50,
    kind: 'archive_comments',
    rarity: 'epic',
    revealedBy: 'archive_comments_25',
    badgeEmoji: 'COMMENT50',
  },
  {
    id: 'archive_comments_100',
    title: 'Archive Oracle',
    description: 'Post 100 archive comments.',
    target: 100,
    kind: 'archive_comments',
    rarity: 'legendary',
    revealedBy: 'archive_comments_50',
    badgeEmoji: 'COMMENT100',
  },
  {
    id: 'palette_master_3x',
    title: 'Palette Master',
    description: 'Use every palette color at least 3 times.',
    target: 32,
    kind: 'palette_mastery',
    rarity: 'rare',
    revealedBy: null,
    rewardType: 'name_color',
    rewardNameColorId: 'dark_green',
    badgeEmoji: 'PALETTE',
  },
  {
    id: 'daily_top_three_finish',
    title: 'Podium Finish',
    description: 'Finish in the top 3 of the daily leaderboard.',
    target: 1,
    kind: 'daily_top_three_finish',
    rarity: 'epic',
    revealedBy: null,
    rewardType: 'name_color',
    rewardNameColorId: 'light_blue',
    badgeEmoji: 'TOP3',
  },
  {
    id: 'active_days_distinct_20',
    title: 'Steady Presence',
    description: 'Be active on 20 distinct days.',
    target: 20,
    kind: 'active_days_distinct',
    rarity: 'rare',
    revealedBy: null,
    rewardType: 'name_color',
    rewardNameColorId: 'deep_teal',
    badgeEmoji: 'ACTIVE20',
  },
  {
    id: 'perfect_daily_ink_pristine',
    title: 'Perfect Finish',
    description: 'Legacy ink-system milestone for a perfect daily finish.',
    target: 1,
    kind: 'perfect_daily_ink_pristine',
    rarity: 'epic',
    revealedBy: null,
    rewardType: 'name_color',
    rewardNameColorId: 'champagne',
    badgeEmoji: 'CHAMPAGNE',
  },
  {
    id: 'golden_wall',
    title: 'Golden Wall',
    description: 'Legacy ink-system milestone for 7 perfect daily finishes.',
    target: 7,
    kind: 'golden_wall',
    rarity: 'legendary',
    revealedBy: null,
    rewardType: 'name_color',
    rewardNameColorId: 'gold',
    badgeEmoji: 'GOLDENWALL',
  },
] as const;

export type AchievementId = (typeof ACHIEVEMENT_DEFINITIONS)[number]['id'];
export type AchievementKind = (typeof ACHIEVEMENT_DEFINITIONS)[number]['kind'];

export interface AchievementProgress {
  id: AchievementId;
  progress: number;
  target: number;
  unlockedAt: string | null;
}

export interface UserAchievement extends AchievementProgress {
  title: string;
  description: string;
  kind: AchievementKind;
  rarity: AchievementRarity;
  revealedBy: AchievementId | null;
  rewardType: AchievementRewardType;
  rewardNameColorId?: NameColorId;
  badgeEmoji: string;
  selectedSlot: 1 | 2 | 3 | null;
}

export interface UserBadge {
  id: AchievementId;
  slot: 1 | 2 | 3;
}

// ============================================
// Color Palette (32 colors) - Premium Collection
// ============================================

export const COLOR_PALETTE = [
  // Row 1: Essentials - The Foundations
  '#FFFFFF', // Pure White
  '#B8C5D6', // Silver Mist
  '#4A5568', // Graphite
  '#0D0D0D', // Obsidian

  // Row 2: Precious Metals - Luxury Collection
  '#FFF8E7', // Champagne
  '#FFD700', // 24K Gold
  '#F59E0B', // Amber
  '#92400E', // Antique Bronze

  // Row 3: Rose Collection - Modern Premium
  '#FECDD3', // Soft Blush
  '#FB7185', // Rose Quartz
  '#E11D48', // Ruby Rose
  '#881337', // Burgundy Velvet

  // Row 4: Crimson Collection - Power
  '#FCA5A5', // Coral Glow
  '#EF4444', // Vermillion
  '#B91C1C', // Ruby Red
  '#450A0A', // Blood Moon

  // Row 5: Emerald Collection - Prestige
  '#A7F3D0', // Mint Crystal
  '#10B981', // Emerald
  '#047857', // Forest Jade
  '#022C22', // Deep Evergreen

  // Row 6: Cyan Collection - Tech Luxe
  '#A5F3FC', // Electric Ice
  '#06B6D4', // Turquoise
  '#0E7490', // Deep Teal
  '#164E63', // Ocean Depth

  // Row 7: Sapphire Collection - Royal
  '#BFDBFE', // Sky Diamond
  '#3B82F6', // Electric Sapphire
  '#1D4ED8', // Royal Blue
  '#0C1445', // Midnight Void

  // Row 8: Violet Collection - Royalty
  '#E9D5FF', // Lavender Dreams
  '#A855F7', // Electric Violet
  '#7C3AED', // Royal Purple
  '#1E0A3C', // Deep Cosmos
] as const;

export type PaletteColor = (typeof COLOR_PALETTE)[number];

// ============================================
// User Types
// ============================================

export type AdminRole = 'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR' | null;
export type LinkedAuthProvider = 'email_password' | 'google';

export interface User {
  id: string;
  username: string;
  email: string;
  authProvider: 'google' | 'email';
  /** Linked auth methods for this account (when provided by API). */
  linkedAuthProviders?: LinkedAuthProvider[];
  adminRole: AdminRole;
  profilePicture: string | null;
  inkBalance: number;
  createdAt: string;
  /** ISO timestamp until which the user cannot paint or comment. null if not timed out. */
  timeoutUntil?: string | null;
  /** If true, user is hidden from daily and all-time leaderboards. */
  hiddenFromLeaderboard?: boolean;
  /** When the email was verified (one-time). null = not verified. OAuth users are treated as verified. */
  emailVerifiedAt?: string | null;
  /** Up to 3 selected achievement badges shown next to username. */
  selectedBadges?: UserBadge[];
  /** Selected username color (must be unlocked), null uses default theme text color. */
  usernameColor?: string | null;
  /** Optional username effect unlocked by special achievements. */
  usernameEffect?: UsernameEffect | null;
  /** Unlocked username colors available for selection. */
  unlockedNameColors?: NameColorReward[];
}

export interface LinkedAuthProviderInfo {
  provider: LinkedAuthProvider;
  email: string | null;
  emailVerifiedAt: string | null;
  linkedAt: string;
  lastUsedAt: string;
}

export interface LinkedAuthProvidersResponse {
  providers: LinkedAuthProviderInfo[];
  hasPassword: boolean;
}

export interface PublicUser {
  id: string;
  username: string;
  adminRole?: AdminRole;
  profilePicture?: string | null;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
  inkBalance?: number;
}

/** User info shown in the live connected-users modal (authenticated users only). Avatar loaded client-side via /api/users/:id/avatar. */
export interface ConnectedUser {
  id: string;
  username: string;
  email: string;
}

// ============================================
// Canvas Types
// ============================================

export interface Pixel {
  x: number;
  y: number;
  color: PaletteColor;
  userId: string;
  placedAt: string;
}

export interface PixelPlacement {
  x: number;
  y: number;
  color: PaletteColor;
  expansionCount?: number;
}

export interface PixelUpdate extends PixelPlacement {
  userId: string;
  username: string;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
  placedAt: string;
}

// Metadata for a single pixel (who placed it and when)
export interface PixelMeta {
  username: string;
  placedAt: string;
  /** User id who placed the pixel (for lazy-loading avatar via API). */
  userId?: string;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
}

// Full canvas state with metadata
export interface CanvasState {
  pixels: (PaletteColor | null)[][];
  pixelMeta: Record<string, PixelMeta>; // key: "x,y"
  lastUpdated: string;
  width: number;
  height: number;
  activePixels: number;
  totalPixels: number;
  completionRatio: number;
  expansionCount: number;
  completionThreshold: number;
  expandByPixels: number;
}

export interface CanvasExpansionEvent extends CanvasState {
  previousWidth: number;
  previousHeight: number;
  expandedByPixels: number;
}

/** A single recorded canvas expansion event (who triggered it and when). */
export interface CanvasExpansionHistoryEntry {
  /** Sequential expansion number (1 = first expansion). */
  expansionNumber: number;
  /** The user who triggered the expansion, or null for automatic/system expansions or deleted users. */
  triggeredBy: {
    userId: string;
    username: string;
    profilePicture: string | null;
    usernameColor?: string | null;
    usernameEffect?: UsernameEffect | null;
  } | null;
  previousWidth: number;
  previousHeight: number;
  newWidth: number;
  newHeight: number;
  /** ISO timestamp of when the expansion occurred. */
  createdAt: string;
}

// ============================================
// Archive Types
// ============================================

export interface ArchiveEntry {
  id: string;
  date: string;
  imageUrl: string;
  totalPixels: number;
  uniqueUsers: number;
  commentCount: number;
  createdAt: string;
  width: number;
  height: number;
  /** Present for admins only; true when gallery is hidden from non-admins */
  hidden?: boolean;
}

export interface ArchivePixelData {
  pixels: (PaletteColor | null)[][];
  pixelMeta: Record<string, PixelMeta>; // key: "x,y"
  width: number;
  height: number;
}

// ============================================
// Comment Types
// ============================================

export type CommentVote = 1 | -1;

export interface Comment {
  id: string;
  archiveId: string;
  userId: string;
  username: string;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
  profilePicture: string | null;
  content: string;
  createdAt: string;
  upvotes: number;
  downvotes: number;
  /** Current user's vote (when authenticated). */
  userVote?: CommentVote | null;
}

// ============================================
// Bug Report Types
// ============================================

export type BugReportStatus = 'open' | 'resolved';

export interface BugReport {
  id: string;
  userId: string;
  username: string;
  email: string;
  message: string;
  pageUrl: string | null;
  userAgent: string | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  status: BugReportStatus;
  createdAt: string;
  resolvedAt: string | null;
}

export interface CreateBugReportRequest {
  message: string;
  screenshotDataUrl: string;
  pageUrl?: string | null;
  viewportWidth?: number | null;
  viewportHeight?: number | null;
}

// ============================================
// Chat Types & Config
// ============================================

export const CHAT_CONFIG = {
  MESSAGE_MAX_LENGTH: 500,
  HISTORY_MAX_MESSAGES: 100,
  /** Number of messages to load when opening chat (from DB). */
  HISTORY_LOAD_COUNT: 50,
} as const;

export interface ChatMessageReply {
  id: string;
  userId: string;
  username: string;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
  content: string;
}

export interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
  content: string;
  createdAt: string;
  replyToMessageId?: string | null;
  replyTo?: ChatMessageReply | null;
}

// ============================================
// WebSocket Events
// ============================================

// Client -> Server
export interface ClientToServerEvents {
  'pixel:place': (data: { x: number; y: number; color: PaletteColor; yolo?: boolean }) => void;
  'pixel:placeBatch': (data: { pixels: PixelPlacement[]; yolo?: boolean }) => void;
  'canvas:request': () => void;
  'admin:archive': () => void; // Admin: manually trigger archive and reset
  'admin:expandCanvas': () => void; // Admin: manually expand canvas for testing
  'chat:message': (data: { content: string; replyToMessageId?: string | null }) => void;
  'chat:deleteMessage': (data: { messageId: string }) => void; // Admin: delete a single chat message
  'live:request': () => void; // Request current list of connected users
}

// Server -> Client
export interface ServerToClientEvents {
  'chat:history': (data: { messages: ChatMessage[] }) => void;
  'chat:message': (data: ChatMessage) => void;
  'chat:rejected': (data: { reason: string }) => void;
  'chat:messagesRemoved': (data: { messageIds: string[] }) => void;
  'pixel:update': (data: PixelUpdate) => void;
  'pixel:batchUpdate': (data: { pixels: PixelUpdate[] }) => void;
  'pixel:rejected': (data: {
    reason: string;
    inkBalance?: number;
    requiresVerification?: boolean;
  }) => void;
  'canvas:state': (data: CanvasState) => void;
  'canvas:expanded': (data: CanvasExpansionEvent) => void;
  'canvas:reset': (data: {
    date: string;
    archiveId: string;
    width: number;
    height: number;
    expansionCount: number;
  }) => void;
  'user:authenticated': (data: { user: PublicUser }) => void;
  'user:inkUpdate': (data: { inkBalance: number }) => void;
  'user:achievementUnlocked': (data: { achievementId: AchievementId }) => void;
  'admin:archiveSuccess': (data: { archiveId: string; date: string }) => void;
  'admin:archiveFailed': (data: { reason: string }) => void;
  'admin:expandSuccess': (data: {
    width: number;
    height: number;
    expandedByPixels: number;
  }) => void;
  'admin:expandFailed': (data: { reason: string }) => void;
  'comment:new': (data: { archiveId: string; comment: Comment }) => void;
  'live:users': (data: { users: ConnectedUser[] }) => void;
  'live:userJoined': (data: ConnectedUser) => void;
  'live:userLeft': (data: { userId: string }) => void;
  error: (data: { message: string }) => void;
}

// ============================================
// Leaderboard Types
// ============================================

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  profilePicture: string | null;
  usernameColor?: string | null;
  usernameEffect?: UsernameEffect | null;
  selectedBadges?: UserBadge[];
  count: number;
}

// ============================================
// API Types
// ============================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  username: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}
